// lib/brands/activeStoreLocations.ts
import { parseReviewVerdict } from '#lib/brands/locationReview';

type MappableStoreLocation = {
  id?: string;
  brandId?: string;
  latitude?: number | null;
  longitude?: number | null;
  isClosed?: boolean;
  closedAt?: string | null;
  source?: string | null;
  reviewVerdict?: 'GO' | 'CLOSED' | 'NOGO' | null;
};

export function hasStoreCoordinates(location: MappableStoreLocation): boolean {
  const { latitude: lat, longitude: lng } = location;
  return typeof lat === 'number' && Number.isFinite(lat) && Math.abs(lat) < 90
    && typeof lng === 'number' && Number.isFinite(lng) && Math.abs(lng) <= 180
    && !(lat === 0 && lng === 0);
}

/** A supplied inventory array is authoritative, even when it has become empty. */
export function resolveActiveStoreLocations<T extends MappableStoreLocation>(
  manual: readonly T[],
  inventory?: readonly T[],
  /** Partial hydration: only these brands' lists are authoritative. */
  loadedBrandIds?: readonly string[],
): T[] {
  const byId = new Map<string | T, T>();
  const fallback = loadedBrandIds
    ? manual.filter(location => !location.brandId || !loadedBrandIds.includes(location.brandId)) : [];
  for (const location of inventory ? [...fallback, ...inventory] : manual) byId.set(location.id || location, location);
  return [...byId.values()].filter(location => {
    const marker = parseReviewVerdict(location.source);
    return !location.isClosed && !location.closedAt
      && marker !== 'CLOSED' && marker !== 'NOGO'
      && location.reviewVerdict !== 'CLOSED' && location.reviewVerdict !== 'NOGO'
      && hasStoreCoordinates(location);
  });
}
